import * as THREE from 'three';
import type { NervousSystem } from './anatomy';
import type { PathwayHandle } from './nervousSystem';

const MAX_PULSES = 900;

interface Pulse {
  pathwayIndex: number;
  /** Normalized position along the pathway, 0-1. */
  t: number;
  /** Fraction of the pathway travelled per second. */
  speed: number;
  brightness: number;
}

/**
 * Travelling action-potential pulses that run along the nerve pathways.
 *
 * Firing rate and conduction speed are driven by simulated receptor occupancy
 * per subdivision, so a stimulant visibly accelerates traffic while a depressant
 * slows it — the motion carries the pharmacology rather than just decorating it.
 *
 * Conduction is illustrative, not to scale: real myelinated axons conduct at
 * tens of metres per second, which at this scale would be a blur.
 */
export class SignalPulses {
  readonly points: THREE.Points;

  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsMaterial;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly pulses: Pulse[] = [];
  private readonly pathways: PathwayHandle[];
  private spawnAccumulator = 0;
  private readonly scratch = new THREE.Vector3();

  constructor(pathways: PathwayHandle[], glowTexture: THREE.Texture) {
    this.pathways = pathways;
    this.positions = new Float32Array(MAX_PULSES * 3);
    this.colors = new Float32Array(MAX_PULSES * 3);

    // Park unused pulses far off-screen rather than at the origin, where they
    // would otherwise pile into a bright blob at the centre of the brain.
    for (let i = 0; i < MAX_PULSES; i++) this.positions[i * 3 + 1] = -9999;

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));

    this.material = new THREE.PointsMaterial({
      size: 0.055,
      map: glowTexture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      transparent: true,
      vertexColors: true,
      opacity: 0.95,
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  /**
   * @param activity  Per-subdivision engagement, 0-1, from the dose-response model.
   * @param tint      Colour of the active substance.
   */
  update(
    deltaSeconds: number,
    activity: Record<NervousSystem, number>,
    tint: THREE.ColorRepresentation,
    visible: boolean,
  ): void {
    this.points.visible = visible;
    if (!visible) return;

    const tintColor = new THREE.Color(tint);

    this.advanceExisting(deltaSeconds);
    this.spawn(deltaSeconds, activity);
    this.writeBuffers(tintColor);
  }

  private advanceExisting(deltaSeconds: number): void {
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const pulse = this.pulses[i];
      pulse.t += pulse.speed * deltaSeconds;
      if (pulse.t >= 1) this.pulses.splice(i, 1);
    }
  }

  private spawn(deltaSeconds: number, activity: Record<NervousSystem, number>): void {
    // Baseline traffic even at rest — a nervous system with no activity at all
    // would be misleading. Activity scales it from there.
    const rates = this.pathways.map((pathway) => 0.5 + 7 * activity[pathway.system]);
    const totalRate = rates.reduce((sum, rate) => sum + rate, 0);

    this.spawnAccumulator += totalRate * deltaSeconds;

    while (this.spawnAccumulator >= 1 && this.pulses.length < MAX_PULSES) {
      this.spawnAccumulator -= 1;

      // Pick a pathway weighted by its own firing rate.
      let target = Math.random() * totalRate;
      let index = 0;
      for (let i = 0; i < rates.length; i++) {
        target -= rates[i];
        if (target <= 0) {
          index = i;
          break;
        }
      }

      const level = activity[this.pathways[index].system];
      this.pulses.push({
        pathwayIndex: index,
        t: 0,
        speed: 0.18 + 0.5 * level + Math.random() * 0.12,
        brightness: 0.55 + 0.45 * level,
      });
    }
  }

  private writeBuffers(tintColor: THREE.Color): void {
    for (let i = 0; i < MAX_PULSES; i++) {
      if (i < this.pulses.length) {
        const pulse = this.pulses[i];
        const pathway = this.pathways[pulse.pathwayIndex];
        pathway.curve.getPointAt(Math.min(0.999, pulse.t), this.scratch);

        // Fade in and out at the ends so pulses do not pop into existence.
        const envelope = Math.sin(Math.PI * pulse.t);
        const intensity = pulse.brightness * (0.35 + 0.65 * envelope);

        this.positions[i * 3] = this.scratch.x;
        this.positions[i * 3 + 1] = this.scratch.y;
        this.positions[i * 3 + 2] = this.scratch.z;
        this.colors[i * 3] = tintColor.r * intensity;
        this.colors[i * 3 + 1] = tintColor.g * intensity;
        this.colors[i * 3 + 2] = tintColor.b * intensity;
      } else {
        this.positions[i * 3 + 1] = -9999;
        this.colors[i * 3] = 0;
        this.colors[i * 3 + 1] = 0;
        this.colors[i * 3 + 2] = 0;
      }
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
  }
}
